import { memo, useState, useRef, useCallback, useMemo } from 'react';
import { Maximize2 } from 'lucide-react';
import { ProtractorSvg, toRad, toDeg, polarPoint, normDeg, snapTo } from '../../../../../VueQuestion/interactifs/SharedProtractor';
import PleinEcranInteractif from '../../../../../VueQuestion/interactifs/PleinEcranInteractif';

/**
 * Rapporteur virtuel interactif.
 *
 * L'élève manipule le rapporteur (drag + rotate) pour mesurer un angle dessiné
 * dans le SVG. Le composant est un OUTIL D'AIDE intégré à l'énoncé — il n'émet
 * aucune réponse. La saisie se fait via le champ `texte` du payload (independent).
 *
 * @param {Object} config
 * @param {{w:number,h:number}} [config.viewBox] — défaut { w: 620, h: 420 }
 * @param {{x:number,y:number}} config.vertex — sommet de l'angle
 * @param {string} [config.vertexLabel] — étiquette du sommet (ex: 'A')
 * @param {{angleDeg:number,labelEnd?:string}} config.ray1 — 1er côté
 * @param {{angleDeg:number,labelEnd?:string}} config.ray2 — 2nd côté
 * @param {number} [config.rayLength=200] — longueur des demi-droites en px
 * @param {boolean} [config.showHintArc=true] — affiche un arc "?" entre les rays
 * @param {'dark'|'light'} [theme='dark']
 */
const PROT_RADIUS = 130;           // rayon en px du rapporteur
const SNAP_TO_VERTEX_PX = 10;      // distance de snap magnétique sur le sommet
const CENTERED_TOLERANCE_PX = 15;  // au-delà, le read-out est masqué

const RapporteurInteractifElementEleve = memo(function RapporteurInteractifElementEleve({ config, theme = 'dark' }) {
    const {
        viewBox = { w: 620, h: 420 },
        vertex,
        vertexLabel,
        ray1,
        ray2,
        rayLength = 200,
        showHintArc = true,
    } = config || {};
    const { w: W, h: H } = viewBox;
    const svgRef = useRef(null);
    const light = theme === 'light';
    const [isExpanded, setIsExpanded] = useState(false); // mode plein écran (confort mobile)

    // Position + rotation du rapporteur (centre initialisé sur le sommet)
    const [protPos, setProtPos] = useState({ x: vertex.x, y: vertex.y });
    const [protRotation, setProtRotation] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [rotating, setRotating] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Extrémités des 2 côtés de l'angle (en coords SVG)
    const ray1End = useMemo(() => polarPoint(vertex.x, vertex.y, rayLength, ray1.angleDeg), [vertex, rayLength, ray1]);
    const ray2End = useMemo(() => polarPoint(vertex.x, vertex.y, rayLength, ray2.angleDeg), [vertex, rayLength, ray2]);

    // Conversion pointer event → coords SVG (responsive + consciente du letterbox `meet` en plein écran)
    const svgCoords = useCallback((e) => {
        const svg = svgRef.current;
        if (!svg) return { x: 0, y: 0 };
        const rect = svg.getBoundingClientRect();
        if (!rect.width || !rect.height) return { x: 0, y: 0 };
        const scale = Math.min(rect.width / W, rect.height / H);
        const offsetX = (rect.width - W * scale) / 2;
        const offsetY = (rect.height - H * scale) / 2;
        return {
            x: (e.clientX - rect.left - offsetX) / scale,
            y: (e.clientY - rect.top - offsetY) / scale,
        };
    }, [W, H]);

    // Distance entre le centre du rapporteur et le sommet (pour affichage conditionnel du read-out)
    const distToVertex = Math.hypot(protPos.x - vertex.x, protPos.y - vertex.y);
    const centered = distToVertex < CENTERED_TOLERANCE_PX;

    // Détection "rapporteur bien placé" — pédagogiquement :
    //  - une des 2 extrémités de la base (outer-0 ou inner-0) est PILE sur un côté
    //  - l'autre côté est dans l'arc [0°, 180°] du rapporteur (donc lisible sur les graduations)
    // Tolérance stricte 0.5° : le générateur doit arrondir ses ray.angleDeg à l'entier
    // pour que le snap au degré de protRotation atteigne un match exact.
    // Retourne 'ray1' | 'ray2' | null (le ray sur lequel la base est alignée).
    const wellPlaced = useMemo(() => {
        if (!centered) return null;
        const TOL = 0.5;
        const rel1 = normDeg(ray1.angleDeg - protRotation);
        const rel2 = normDeg(ray2.angleDeg - protRotation);
        const isAtBase = (rel) => rel < TOL || rel > 360 - TOL || Math.abs(rel - 180) < TOL;
        const isInside = (rel) => rel >= 0 && rel <= 180;
        if (isAtBase(rel1) && isInside(rel2)) return 'ray1';
        if (isAtBase(rel2) && isInside(rel1)) return 'ray2';
        return null;
    }, [centered, protRotation, ray1.angleDeg, ray2.angleDeg]);

    /**
     * Lecture du rapporteur. Deux corrections par rapport à la version d'origine,
     * qui ne lisait QUE `ray2` et ignorait par quelle extrémité de la base le
     * zéro était aligné :
     *
     *  - `wellPlaced` accepte les DEUX alignements ('ray1' ou 'ray2'). On lit
     *    donc le côté qui n'est pas sur la base, quel qu'il soit.
     *  - le rapporteur porte deux échelles, un zéro à droite et un à gauche.
     *    Aligner le zéro de gauche est aussi valide, mais la mesure vaut alors
     *    180 moins la graduation lue.
     *
     * `graduation` est la position à surligner en rouge (index de l'échelle
     * extérieure), `mesure` est la valeur de l'angle. Les deux diffèrent quand
     * l'élève mesure depuis le zéro de gauche.
     */
    const lecture = useMemo(() => {
        if (!centered) return { graduation: null, mesure: null };
        const relDe = (p) => normDeg(
            toDeg(Math.atan2(-(p.y - vertex.y), p.x - vertex.x)) - protRotation
        );
        if (!wellPlaced) {
            const rel = relDe(ray2End);
            return (rel >= 0 && rel <= 180)
                ? { graduation: Math.round(rel), mesure: Math.round(rel) }
                : { graduation: null, mesure: null };
        }
        const relBase = wellPlaced === 'ray1' ? relDe(ray1End) : relDe(ray2End);
        const relAutre = wellPlaced === 'ray1' ? relDe(ray2End) : relDe(ray1End);
        if (relAutre < 0 || relAutre > 180) return { graduation: null, mesure: null };
        const depuisZeroDroite = (relBase < 90 || relBase > 270);
        return {
            graduation: Math.round(relAutre),
            mesure: Math.round(depuisZeroDroite ? relAutre : 180 - relAutre),
        };
    }, [centered, wellPlaced, ray1End, ray2End, vertex, protRotation]);

    const readAngle = lecture.graduation;

    // ─── Gestion des gestes pointer ────────────────────────────────
    const onBodyPointerDown = useCallback((e) => {
        e.stopPropagation();
        const pt = svgCoords(e);
        setDragging(true);
        setDragOffset({ x: pt.x - protPos.x, y: pt.y - protPos.y });
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* no-op */ }
    }, [svgCoords, protPos]);

    const onRotateHandlePointerDown = useCallback((e) => {
        e.stopPropagation();
        setRotating(true);
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* no-op */ }
    }, []);

    const onPointerMove = useCallback((e) => {
        if (!dragging && !rotating) return;
        const pt = svgCoords(e);
        if (dragging) {
            setProtPos({ x: pt.x - dragOffset.x, y: pt.y - dragOffset.y });
        }
        if (rotating) {
            const angle = toDeg(Math.atan2(-(pt.y - protPos.y), pt.x - protPos.x));
            setProtRotation(snapTo(normDeg(angle), 1));
        }
    }, [dragging, rotating, dragOffset, protPos, svgCoords]);

    const onPointerUp = useCallback(() => {
        // Snap magnétique sur le sommet si on est proche
        if (dragging) {
            const d = Math.hypot(protPos.x - vertex.x, protPos.y - vertex.y);
            if (d < SNAP_TO_VERTEX_PX) {
                setProtPos({ x: vertex.x, y: vertex.y });
            }
        }
        setDragging(false);
        setRotating(false);
    }, [dragging, protPos, vertex]);

    // Coords de la poignée de rotation dans le référentiel SVG global
    const rotHandle = useMemo(() => ({
        x: protPos.x + (PROT_RADIUS + 16) * Math.cos(toRad(-protRotation)),
        y: protPos.y + (PROT_RADIUS + 16) * Math.sin(toRad(-protRotation)),
    }), [protPos, protRotation]);

    // Petite flèche au bout de chaque côté de l'angle
    const arrowTip = (end) => {
        const dx = end.x - vertex.x, dy = end.y - vertex.y;
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len, uy = dy / len;
        const tipLen = 10;
        return `${end.x},${end.y} ${end.x - tipLen * ux + tipLen * 0.35 * uy},${end.y - tipLen * uy - tipLen * 0.35 * ux} ${end.x - tipLen * ux - tipLen * 0.35 * uy},${end.y - tipLen * uy + tipLen * 0.35 * ux}`;
    };

    // Arc "hint" entre les deux côtés (va de ray1 vers ray2 dans le sens direct)
    const hintArcPath = useMemo(() => {
        if (!showHintArc) return null;
        const r = 35;
        const delta = normDeg(ray2.angleDeg - ray1.angleDeg);
        const large = delta > 180 ? 1 : 0;
        const sweep = 0; // sens inverse du y SVG
        const start = polarPoint(vertex.x, vertex.y, r, ray1.angleDeg);
        const end = polarPoint(vertex.x, vertex.y, r, ray2.angleDeg);
        return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} ${sweep} ${end.x} ${end.y}`;
    }, [showHintArc, vertex, ray1, ray2]);

    const hintLabelPos = useMemo(() => {
        const mid = (ray1.angleDeg + ray2.angleDeg) / 2;
        return polarPoint(vertex.x, vertex.y, 48, mid);
    }, [vertex, ray1, ray2]);

    const bodyBg = light ? 'bg-white' : 'bg-slate-50';
    const borderCls = light ? 'border-gray-300' : 'border-slate-400';

    const canvas = (
        <div className={isExpanded
            ? `flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden rounded-xl border-2 ${bodyBg} ${borderCls}`
            : `my-3 w-full rounded-xl border-2 overflow-hidden ${bodyBg} ${borderCls}`}>
            <svg
                ref={svgRef}
                viewBox={`0 0 ${W} ${H}`}
                className={isExpanded ? '' : 'w-full'}
                preserveAspectRatio="xMidYMid meet"
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                style={isExpanded ? { touchAction: 'none', userSelect: 'none', width: '100%', height: '100%' } : { touchAction: 'none', userSelect: 'none' }}
            >
                {/* Grille de fond discrète */}
                <defs>
                    <pattern id="rapp-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                        <circle cx="10" cy="10" r="0.6" fill="#e2e8f0" />
                    </pattern>
                </defs>
                <rect width={W} height={H} fill="url(#rapp-grid)" />

                {/* Côtés de l'angle */}
                <line x1={vertex.x} y1={vertex.y} x2={ray1End.x} y2={ray1End.y} stroke="#1e293b" strokeWidth={2.5} strokeLinecap="round" />
                <line x1={vertex.x} y1={vertex.y} x2={ray2End.x} y2={ray2End.y} stroke="#1e293b" strokeWidth={2.5} strokeLinecap="round" />
                <polygon points={arrowTip(ray1End)} fill="#1e293b" />
                <polygon points={arrowTip(ray2End)} fill="#1e293b" />

                {/* Label du sommet */}
                {vertexLabel && (
                    <text
                        x={vertex.x - 14} y={vertex.y + 20}
                        fontSize={16} fontWeight={700}
                        fill="#1e3a5f" fontFamily="system-ui, sans-serif"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                        {vertexLabel}
                    </text>
                )}
                {/* Labels des extrémités */}
                {ray1.labelEnd && (
                    <text
                        x={ray1End.x + 6} y={ray1End.y + 4}
                        fontSize={14} fontWeight={600}
                        fill="#1e3a5f" fontFamily="system-ui, sans-serif"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >{ray1.labelEnd}</text>
                )}
                {ray2.labelEnd && (
                    <text
                        x={ray2End.x + 6} y={ray2End.y + 4}
                        fontSize={14} fontWeight={600}
                        fill="#1e3a5f" fontFamily="system-ui, sans-serif"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >{ray2.labelEnd}</text>
                )}

                {/* Arc hint "?" */}
                {hintArcPath && (
                    <g style={{ pointerEvents: 'none' }}>
                        <path d={hintArcPath} fill="none" stroke="#3b82f6" strokeWidth={1.5} opacity={0.55} />
                        <text
                            x={hintLabelPos.x} y={hintLabelPos.y}
                            fontSize={13} fill="#3b82f6" fontWeight={700} textAnchor="middle" dominantBaseline="central"
                            fontFamily="system-ui, sans-serif"
                        >?</text>
                    </g>
                )}

                {/* Corps du rapporteur (drag) */}
                <g
                    onPointerDown={onBodyPointerDown}
                    style={{ cursor: dragging ? 'grabbing' : 'grab' }}
                >
                    <ProtractorSvg
                        cx={protPos.x} cy={protPos.y}
                        rotation={protRotation}
                        radius={PROT_RADIUS}
                        readAngle={readAngle}
                        theme={theme}
                    />
                </g>

                {/* Overlay invisible pour capter le drag de la poignée rotation (hitbox élargie) */}
                <circle
                    cx={rotHandle.x} cy={rotHandle.y} r={26}
                    fill="transparent"
                    style={{ cursor: 'grab' }}
                    onPointerDown={onRotateHandlePointerDown}
                />

                {/* Badge MESURE (rouge, toujours affiché si centré) — juste la lecture brute */}
                {centered && lecture.mesure !== null && (
                    <g style={{ pointerEvents: 'none' }}>
                        <rect x={W - 88} y={H - 40} width={72} height={28} rx={6} fill="#ef4444" opacity={0.95} />
                        <text
                            x={W - 52} y={H - 21}
                            textAnchor="middle"
                            fontSize={14} fontWeight={700}
                            fill="white" fontFamily="system-ui, sans-serif"
                        >
                            {lecture.mesure}°
                        </text>
                    </g>
                )}
                {/* Badge PLACEMENT (indépendant) — vert si bien placé, gris sinon.
                    L'élève sait que sa lecture est fiable quand ce badge est vert. */}
                {centered && (
                    <g style={{ pointerEvents: 'none' }}>
                        <rect
                            x={W - 240} y={H - 40} width={140} height={28} rx={6}
                            fill={wellPlaced ? '#16a34a' : '#94a3b8'} opacity={0.95}
                        />
                        <text
                            x={W - 170} y={H - 21}
                            textAnchor="middle"
                            fontSize={12} fontWeight={700}
                            fill="white" fontFamily="system-ui, sans-serif"
                        >
                            {wellPlaced ? '✓ Bien placé' : '⋯ Ajuste la base'}
                        </text>
                    </g>
                )}
                {/* Halo vert sur l'extrémité du côté sur lequel le 0 du rapporteur est aligné */}
                {wellPlaced && (
                    <circle
                        cx={wellPlaced === 'ray1' ? ray1End.x : ray2End.x}
                        cy={wellPlaced === 'ray1' ? ray1End.y : ray2End.y}
                        r={9}
                        fill="#16a34a"
                        opacity={0.35}
                        style={{ pointerEvents: 'none' }}
                    />
                )}
                {/* Hint "rapporteur pas centré" si déplacé loin du sommet */}
                {!centered && (
                    <g style={{ pointerEvents: 'none' }}>
                        <rect x={W - 210} y={H - 40} width={194} height={28} rx={6} fill="#94a3b8" opacity={0.9} />
                        <text
                            x={W - 113} y={H - 21}
                            textAnchor="middle"
                            fontSize={12} fontWeight={600}
                            fill="white" fontFamily="system-ui, sans-serif"
                        >
                            Centre le rapporteur sur le sommet
                        </text>
                    </g>
                )}
            </svg>
        </div>
    );

    return isExpanded ? (
        <PleinEcranInteractif open onClose={() => setIsExpanded(false)} title="Rapporteur">
            <div className="h-full flex flex-col gap-3">
                {canvas}
                <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 transition shadow-lg shadow-blue-500/30"
                >
                    Terminé
                </button>
            </div>
        </PleinEcranInteractif>
    ) : (
        <div className="relative">
            {canvas}
            <button
                type="button"
                onClick={() => setIsExpanded(true)}
                aria-label="Agrandir le rapporteur"
                className="absolute top-3 right-2 z-10 flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md border bg-white/90 text-blue-700 border-blue-300 hover:bg-white shadow-sm"
            >
                <Maximize2 size={13} /> Agrandir
            </button>
        </div>
    );
});

export default RapporteurInteractifElementEleve;
